import React, { createContext, useEffect, useMemo, useState } from 'react';
import { loginPage } from '../config/routeConfig';
import { initializeApp } from "firebase/app";
import { GoogleAuthProvider, getAuth, signInWithEmailAndPassword, signInWithPopup, signOut, sendPasswordResetEmail, createUserWithEmailAndPassword } from "firebase/auth";
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { getCompanyNameFromBillingoId, getMyRestaurantsRoute, getUserInfo, selectRestaurantRoute } from '../config/apiRoutes';
import { constructFetchOptions } from '../hooks/useFetch';
import { Label } from '../models/Label';
import { ReservationSource } from '../models/ReservationSource';
import { ReservationType } from '../models/ReservationType';
import { RestaurantTable } from '../models/RestaurantTable';
import { Restaurant } from '../models/Restaurant';
import { Announcement } from '../models/Announcement';
import AnnouncementAlert from '../components/announcement/AnnouncementAlert';
import { RightType } from '../models/Auth';
import { RequiredFields } from '../models/Reservation';
import { PaymentScheme } from '../models/PaymentScheme';

type AuthProviderProps = {
  children?: any;
}

export enum AuthState {
  LOADING,
  LOGGED_IN,
  LOGGED_OUT,
}

interface AuthUser {
  email: string;
  token: string;
}

export interface UserInfo {
  active_page_id: string;
  email: string;
  firebase_user_id: string;
  selected_restaurant: SelectedRestaurant;
  announcements: Announcement[];
  right_type: RightType;
  timetable_ontouch_enabled: boolean;
  timetable_allow_eviction: boolean | null;
  timetable_allow_time_change: boolean | null;
  timetable_show_no_table: boolean | null;
  timetable_group_by_time: boolean | null;
  timetable_show_full_day: boolean | null;
}

interface SelectedRestaurant {
  labels: Label[];
  fb_id: string;
  name: string;
  sms?: string;
  turnaroundtime: number;
  custom_sources: ReservationSource[];
  reservation_types: ReservationType[];
  tables: RestaurantTable[];
  autoapplytotable: boolean;
  waitlist: boolean;
  group_reservations: boolean;
  shortname: string;
  outstanding_invoice: number;
  default_reservation_time: number;
  required_fields: RequiredFields;
  payment_schemes: PaymentScheme[];
  show_on_group_reservation_page: boolean;
  resevation_length_step: number | null;
  custom_length_step: number | null;
  reservationlength: number;
  subscription_plan?: string;
}

type AuthContextProps = {
  authState: AuthState;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  user: AuthUser | null;
  getToken: () => Promise<string | undefined>;
  googleLogin: () => Promise<boolean>;
  forgotPasswordEmail: (email: string) => Promise<void>;
  errorMessage: string | null;
  setErrorMessage: (message: string | null) => void;
  userInfo: UserInfo | null;
  tableManagementOn: boolean;
  autoApplyOn: boolean;
  smsSendingOn: boolean;
  isProfessional: boolean;
  isUltimate: boolean;
  fetchUserInfo: () => Promise<void>;
  selectRestaurant: (restaurantId: string) => Promise<void>;
  myRestaurants: Restaurant[];
  registerUser: (email: string, password: string) => Promise<boolean>;
  setSchemeInactive: (schemeId: number) => void;
  fetchCompanyName: () => Promise<string>;
}

const firebaseApp = initializeApp({
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
  measurementId: process.env.REACT_APP_MEASUREMENT_ID
});

const AuthContext = createContext<AuthContextProps>({} as any);

export const AuthProvider = (props: AuthProviderProps) => {
  const { t } = useTranslation();
  const [authState, setAuthState] = React.useState<AuthState>(AuthState.LOADING);
  const [user, setUser] = React.useState<AuthUser | null>(null);
  const [userInfo, setUserInfo] = React.useState<UserInfo | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const [myRestaurants, setMyRestaurants] = useState<Restaurant[]>([]);
  const tableManagementOn = useMemo(() => {
    if (userInfo == null || userInfo.selected_restaurant == null) return false;
    return userInfo?.selected_restaurant.tables.length > 0;
  }, [userInfo]);

  const autoApplyOn = useMemo(() => {
    if (userInfo == null || userInfo.selected_restaurant == null) return false;
    return userInfo?.selected_restaurant.autoapplytotable;
  }, [userInfo]);

  const smsSendingOn = useMemo(() => {
    if (userInfo == null || userInfo.selected_restaurant == null) return false;
    return userInfo?.selected_restaurant.sms != null && userInfo?.selected_restaurant.sms === "smseagle" || userInfo?.selected_restaurant.sms === "seeme";
  }, [userInfo]);

  const isProfessional = useMemo(() => {
    const plan = userInfo?.selected_restaurant?.subscription_plan;
    return plan === 'PROFESSIONAL' || plan === 'ULTIMATE';
  }, [userInfo]);

  const isUltimate = useMemo(() => {
    return userInfo?.selected_restaurant?.subscription_plan === 'ULTIMATE';
  }, [userInfo]);

  useEffect(() => {
    initAuth();
  }, []);

  useEffect(() => {
    if (authState === AuthState.LOGGED_OUT) {
      navigate(loginPage.path);
    }
    if (authState === AuthState.LOGGED_IN) {
      fetchUserInfo();
      fetchMyRestaurants();
    }
  }, [authState]);

  async function fetchUserInfo() {
    const token = await getToken();
    if (token) {
      const response = await fetch(getUserInfo, constructFetchOptions("GET", token) as RequestInit);
      if (response.ok) {
        const userInfo = await response.json();
        if (userInfo.selected_restaurant == null) {
          setErrorMessage(t("auth.no_restaurant_selected"));
          logout();
          return;
        }
        setUserInfo(userInfo);
      } else {
        logout();
      }
    }
  }

  async function selectRestaurant(restaurantId: string) {
    const token = await getToken();
    if (token) {
      const response = await fetch(selectRestaurantRoute, constructFetchOptions("POST", token, {
        email: userInfo?.email,
        restaurant_id: restaurantId
      }) as RequestInit);
      if (response.ok) {
        fetchUserInfo();
      }
    }
  }

  async function fetchMyRestaurants() {
    const token = await getToken();
    if (token) {
      const response = await fetch(getMyRestaurantsRoute, constructFetchOptions("GET", token) as RequestInit);
      if (response.ok) {
        setMyRestaurants(await response.json());
      }
    }
  }

  async function initAuth() {
    getAuth(firebaseApp).onAuthStateChanged((u: any) => {
      if (u != null && u.email != null && u.accessToken != null) {
        setUser({
          email: u.email,
          token: u.accessToken
        });
        setAuthState(AuthState.LOGGED_IN);
        setErrorMessage(null);
      } else {
        setUser(null);
        setAuthState(AuthState.LOGGED_OUT);
      }
    });
  }

  async function getToken() {
    return await getAuth(firebaseApp).currentUser?.getIdToken();
  }

  async function login(email: string, password: string) {
    try {
      const userData = await signInWithEmailAndPassword(getAuth(firebaseApp), email, password);
      if (userData.user == null || userData.user.email == null) {
        return false;
      }
      setUser({
        email: userData.user.email,
        token: await userData.user.getIdToken(),
      });

      return true;
    } catch (e) {
      setErrorMessage(t("auth.invalid_credentials"));
      console.error(e);
      return false;
    }
  }

  async function googleLogin() {
    try {
      const credentials = await signInWithPopup(getAuth(firebaseApp), new GoogleAuthProvider());
      const user = credentials.user;
      if (user == null || user.email == null) {
        return false;
      }
      setUser({
        email: user.email,
        token: await user.getIdToken(),
      });

      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async function logout() {
    await signOut(getAuth(firebaseApp));
    setUser(null);
    navigate(loginPage.path);
  }

  function forgotPasswordEmail(email: string) {
    return sendPasswordResetEmail(getAuth(firebaseApp), email);
  }

  async function registerUser(email: string, password: string) {
    try {
      const userData = await createUserWithEmailAndPassword(getAuth(firebaseApp), email, password);
      if (userData.user == null || userData.user.email == null) {
        return false;
      }
      setUser({
        email: userData.user.email,
        token: await userData.user.getIdToken(),
      });

      return true;
    } catch (e) {
      if (typeof e === "object" && e !== null && "code" in e && (e as any).code === "auth/email-already-in-use") {
        setErrorMessage(t("auth.email_already_in_use"));
        return false;
      } else {
        setErrorMessage(t("auth.registration_failed"));
        return false;
      }

    }
  }

  function setSchemeInactive(schemeId: number) {
    if (userInfo == null || userInfo.selected_restaurant == null) return;
    const updatedSchemes = userInfo.selected_restaurant.payment_schemes.map(scheme => {
      if (scheme.payment_scheme_id === schemeId) {
        return { ...scheme, active: 0 };
      }
      return scheme;
    });
    setUserInfo({
      ...userInfo,
      selected_restaurant: {
        ...userInfo.selected_restaurant,
        payment_schemes: updatedSchemes
      }
    });
  }

  async function fetchCompanyName(): Promise<string> {
    const token = await getToken();
    if (!token) return "";
    const response = await fetch(getCompanyNameFromBillingoId, constructFetchOptions("GET", token) as RequestInit);
    if (response.ok) {
      const data = await response.json();
      return data || "";
    }
    return "";
  }

  const state = {
    authState,
    login,
    logout,
    user,
    getToken,
    googleLogin,
    forgotPasswordEmail,
    errorMessage,
    setErrorMessage,
    userInfo,
    tableManagementOn,
    autoApplyOn,
    smsSendingOn,
    isProfessional,
    isUltimate,
    fetchUserInfo,
    selectRestaurant,
    myRestaurants,
    registerUser,
    setSchemeInactive,
    fetchCompanyName,
  }

  return (
    <AuthContext.Provider value={state}>
      {props.children}
      <AnnouncementAlert announcements={userInfo?.announcements || []} handleRefresh={fetchUserInfo} />
    </AuthContext.Provider>
  );
}

export default AuthContext